import React, { useState, useEffect, useRef } from 'react';
import { X, Package, CheckCircle, AlertCircle, Loader2, ScanLine, Calendar, ArrowRight } from 'lucide-react';
import { stcTransService } from '../../../../../services/stcTransService';
import { ScanTextInput, NumericInput } from '../../../../common/inputs';
import { useTranslation } from '../../../../../contexts/I18nProvider';

export interface BatchTransferResult {
  batchNo: string;
  expiryDate?: string;
  quantity: number;
}

interface BatchTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (batches: BatchTransferResult[]) => void;
  itemNo: string;
  itemDescription: string;
  binCode: string;
  warehouseId: string;
}

interface AvailableBatch {
  batchNo: string;
  expiryDate: string | null;
  quantity: number;
  selectedQty: string;
  selected: boolean;
}

export const BatchTransferModal: React.FC<BatchTransferModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  itemNo,
  itemDescription,
  binCode,
  warehouseId
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevIsOpenRef = useRef(false);
  const [loading, setLoading] = useState(false);

  const [availableBatches, setAvailableBatches] = useState<AvailableBatch[]>([]);
  const [filteredBatches, setFilteredBatches] = useState<AvailableBatch[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setError(null);
      setConfirming(false);
      setSearchTerm('');
      setAvailableBatches([]);
      setFilteredBatches([]);
      loadBatches();
      setTimeout(() => inputRef.current?.focus(), 100);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter batches based on search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredBatches(availableBatches);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredBatches(
        availableBatches.filter(b => b.batchNo.toLowerCase().includes(term))
      );
    }
  }, [searchTerm, availableBatches]);

  const loadBatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const batches = await stcTransService.getBatchesAtBin(itemNo, warehouseId, binCode);
      const mapped: AvailableBatch[] = batches.map(b => ({
        batchNo: b.batchNo,
        expiryDate: b.expiryDate,
        quantity: b.quantity,
        selectedQty: '',
        selected: false
      }));
      setAvailableBatches(mapped);
      setFilteredBatches(mapped);
      if (mapped.length === 0) {
        setError(t('warehouse.modules.warehouseTransfer.modals.batchTransfer.noBatchesAtLocation', {}, 'Ingen batcher tilgjengelig på denne lokasjonen'));
      }
    } catch (err: any) {
      console.error('Error loading batches:', err);
      setError(err.message || t('warehouse.modules.warehouseTransfer.modals.batchTransfer.couldNotLoadBatches', {}, 'Kunne ikke hente batcher'));
    } finally {
      setLoading(false);
    }
  };

  const toggleBatch = (batchNo: string) => {
    const update = (b: AvailableBatch) =>
      b.batchNo === batchNo ? { ...b, selected: !b.selected } : b;
    setAvailableBatches(prev => prev.map(update));
    setFilteredBatches(prev => prev.map(update));
  };

  const updateSelectedQty = (batchNo: string, value: string) => {
    const update = (b: AvailableBatch) =>
      b.batchNo === batchNo ? { ...b, selectedQty: value, selected: true } : b;
    setAvailableBatches(prev => prev.map(update));
    setFilteredBatches(prev => prev.map(update));
  };

  const handleScanInput = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      e.preventDefault();
      const exactMatch = availableBatches.find(
        b => b.batchNo.toLowerCase() === searchTerm.trim().toLowerCase()
      );
      if (exactMatch) {
        toggleBatch(exactMatch.batchNo);
        setSearchTerm('');
        setError(null);
      } else {
        setError(t('warehouse.modules.warehouseTransfer.modals.batchTransfer.batchNotFound', {}, 'Batchnummeret finnes ikke på denne lokasjonen'));
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const formatExpiryDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('no-NO');
    } catch {
      return dateStr;
    }
  };

  const isExpired = (dateStr: string | null | undefined): boolean => {
    if (!dateStr) return false;
    try { return new Date(dateStr) < new Date(); } catch { return false; }
  };

  const handleConfirm = () => {
    const selected = availableBatches.filter(b => b.selected);
    if (selected.length === 0) return;

    for (const b of selected) {
      const qty = parseFloat(b.selectedQty);
      if (isNaN(qty) || qty <= 0) {
        setError(t('warehouse.modules.warehouseTransfer.modals.batchTransfer.invalidQuantityForBatch', { '0': b.batchNo }, `Ugyldig antall for batch ${b.batchNo}`));
        return;
      }
      if (qty > b.quantity) {
        setError(t('warehouse.modules.warehouseTransfer.modals.batchTransfer.quantityExceedsForBatch', { '0': b.batchNo, '1': b.quantity }, `Antall for batch ${b.batchNo} kan ikke overstige tilgjengelig (${b.quantity})`));
        return;
      }
    }

    setConfirming(true);
    onComplete(selected.map(b => ({
      batchNo: b.batchNo,
      expiryDate: b.expiryDate || undefined,
      quantity: parseFloat(b.selectedQty)
    })));
  };

  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  const selectedCount = availableBatches.filter(b => b.selected).length;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-0 sm:p-4"
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden"
        onClick={stopPropagation}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-orange-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Package className="text-orange-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('warehouse.modules.warehouseTransfer.modals.batchTransfer.title', {}, 'Velg batcher for overføring')}
              </h2>
              <p className="text-sm text-gray-500">{itemNo} - {itemDescription}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Item/Location info */}
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>{t('warehouse.modules.warehouseTransfer.modals.batchTransfer.fromLabel', {}, 'Fra:')} <span className="font-medium text-gray-900">{binCode}</span></span>
            <ArrowRight className="h-3 w-3 text-gray-400" />
            {availableBatches.length > 0 && (
              <span className="ml-auto">{t('warehouse.modules.warehouseTransfer.modals.batchTransfer.batchesLabel', {}, 'Batcher:')} <span className="font-medium text-gray-900">{availableBatches.length}</span></span>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <ScanTextInput
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setError(null); }}
              onKeyDown={handleScanInput}
              placeholder={t('warehouse.modules.warehouseTransfer.modals.batchTransfer.searchPlaceholder', {}, 'Skann eller søk batchnummer...')}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {error && (
            <div className="mt-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Batch list */}
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
              <Loader2 className="animate-spin" size={20} />
              {t('warehouse.modules.warehouseTransfer.modals.batchTransfer.loadingBatches', {}, 'Henter batcher...')}
            </div>
          ) : filteredBatches.length === 0 && !error ? (
            <div className="text-center text-gray-500 py-8">
              {searchTerm ? t('warehouse.modules.warehouseTransfer.modals.batchTransfer.noSearchResults', {}, 'Ingen treff på søket') : t('warehouse.modules.warehouseTransfer.modals.batchTransfer.noBatchesAvailable', {}, 'Ingen batcher tilgjengelig')}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredBatches.map((batch) => (
                <div
                  key={batch.batchNo}
                  className={`p-3 rounded-lg border transition-all ${
                    batch.selected
                      ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200'
                      : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => toggleBatch(batch.batchNo)}
                      className="flex items-center gap-3 text-left flex-1"
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        batch.selected ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                      }`}>
                        {batch.selected && <CheckCircle className="text-white" size={14} />}
                      </div>
                      <div>
                        <span className="font-mono font-medium text-gray-900">{batch.batchNo}</span>
                        {batch.expiryDate && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <Calendar size={11} className="text-gray-400" />
                            <span className="text-xs text-gray-500">{formatExpiryDate(batch.expiryDate)}</span>
                            {isExpired(batch.expiryDate) && (
                              <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">{t('warehouse.modules.warehouseTransfer.modals.batchTransfer.expired', {}, 'Utløpt')}</span>
                            )}
                          </div>
                        )}
                        <span className="text-xs text-gray-500">{t('warehouse.modules.warehouseTransfer.modals.batchTransfer.stockLabel', {}, 'Beholdning:')} {batch.quantity}</span>
                      </div>
                    </button>

                    {batch.selected && (
                      <div className="flex items-center gap-2 ml-2">
                        <label className="text-xs text-gray-500">{t('warehouse.modules.warehouseTransfer.modals.batchTransfer.quantityLabel', {}, 'Antall:')}</label>
                        <NumericInput
                          mode="decimal"
                          quantity
                          value={batch.selectedQty}
                          onChange={(e) => updateSelectedQty(batch.batchNo, e.target.value)}
                          placeholder="0"
                          className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                          min="0.01"
                          max={batch.quantity}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedCount === 0 || confirming}
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
              selectedCount > 0
                ? 'bg-orange-600 text-white hover:bg-orange-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {confirming ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <CheckCircle size={16} />
            )}
            {t('warehouse.modules.warehouseTransfer.modals.batchTransfer.transferButton', { '0': selectedCount, '1': selectedCount !== 1 ? 'er' : '' }, `Overfør ${selectedCount} batch${selectedCount !== 1 ? 'er' : ''}`)}
          </button>
        </div>
      </div>
    </div>
  );
};
